import Vue from 'vue'
import { extend, localize, ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  alpha_num as alphaNum,
  confirmed,
  email,
  max,
  max_value as maxValue,
  min,
  min_value as minValue,
  numeric,
  regex,
  required
} from 'vee-validate/dist/rules'

import en from 'vee-validate/dist/locale/en.json'
import ru from 'vee-validate/dist/locale/ru.json'
import de from 'vee-validate/dist/locale/de.json'
import es from 'vee-validate/dist/locale/es.json'
import tr from 'vee-validate/dist/locale/tr.json'
import hr from 'vee-validate/dist/locale/hr.json'
import cs from 'vee-validate/dist/locale/cs.json'
import bg from 'vee-validate/dist/locale/bg.json'

import dayjs from 'dayjs'
import { UserApiService } from '@/data/services/UserApiService'
import i18n from '@/plugins/i18n'

// setInteractionMode('eager')

extend('required', required)
extend('email', email)
extend('min', min)
extend('max', max)
extend('numeric', numeric)
extend('max_value', maxValue)
extend('min_value', minValue)
extend('alpha_num', alphaNum)
extend('confirmed', confirmed)
extend('regex', regex)

extend('time', {
  validate: value => {
    return dayjs(value, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'h:mm:ss A'], true).isValid()
  },
  message: i18n.t('validation.time')
})

extend('date', {
  validate: value => {
    return dayjs(value, 'L', true).isValid()
  },
  message: i18n.t('validation.date')
})

extend('max_selections', {
  validate: (value, args) => {
    if (!Array.isArray(value)) return true

    return value.length <= args.max
  },
  params: ['max'],
  message: (_, params) => i18n.t('validation.max_selection', { num: params.max })
})

extend('ip', {
  validate: value => {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(value)
  },
  message: i18n.t('validation.ip')
})

extend('unique_email', {
  validate: async (value, args) => {
    const result = await UserApiService.checkUnique(args.user_id, { email: value })

    return !!result.email
  },
  params: ['user_id'],
  message: i18n.t('alert.email_used')
})

extend('unique_username', {
  validate: async (value, args) => {
    const result = await UserApiService.checkUnique(args.user_id, { username: value })

    return !!result.username
  },
  params: ['user_id'],
  message: i18n.t('alert.username_used')
})

extend('password_policy', {
  validate: value => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/.test(value)
  },
  message: i18n.t('validation.password_policy')
})

extend('single_integration', {
  validate: (value, args) => {
    if (!value) return true

    return !args.otherFields.some(field => !!args.event[field])
  },
  params: ['field', 'otherFields', 'event'],
  message: i18n.t('validation.single_integration')
})

extend('not_same', {
  validate: (value, { target }) => {
    return value !== target
  },
  params: ['target'],
  message: i18n.t('validation.not_same')
})

en.messages = {
  ...en.messages,
  alpha_num: 'The field may only contain alpha-numeric characters',
  email: 'The field must be a valid email',
  max_value: 'The field must be {max} or less',
  max: 'The field may not be greater than {length} characters',
  min_value: 'The field must be {min} or more',
  min: 'The field must be at least {length} characters',
  numeric: 'The field may only contain numeric characters',
  required: 'The field is required',
  confirmed: 'The confirmation password does not match'
}

ru.messages = {
  ...ru.messages,
  alpha_num: 'Поле может содержать только алфавитно-цифровые символы',
  email: 'Поле должно содержать действительный адрес электронной почты',
  max_value: 'Поле должно быть равно или меньше {max}',
  max: 'Поле не может содержать больше {length} символов',
  min_value: 'Поле должно быть равно или больше {min}',
  min: 'Поле должно содержать хотя бы {length} символов',
  numeric: 'Поле может содержать только числовые символы',
  required: 'Поле обязательно для заполнения',
  confirmed: 'Подтвержденный пароль не совпадает'
}

de.messages = {
  ...de.messages,
  alpha_num: 'Das Feld darf nur alphanumerische Zeichen enthalten',
  email: 'Das Feld muss eine gültige E-Mail-Adresse sein',
  max_value: 'Das Feld muss {max} oder weniger sein',
  max: 'Das Feld darf nicht länger als {length} Zeichen sein',
  min_value: 'Das Feld muss {min} oder mehr sein',
  min: 'Das Feld muss mindestens {length} Zeichen lang sein',
  numeric: 'Das Feld darf nur numerische Zeichen enthalten',
  required: 'Das Feld ist erforderlich',
  confirmed: 'Das Bestätigungspasswort stimmt nicht überein'
}

es.messages = {
  ...es.messages,
  alpha_num: 'El campo solo puede contener caracteres alfanuméricos',
  email: 'El campo debe ser un correo electrónico válido',
  max_value: 'El campo debe ser {max} o menos',
  max: 'El campo no puede tener más de {length} caracteres',
  min_value: 'El campo debe ser {min} o más',
  min: 'El campo debe tener al menos {length} caracteres',
  numeric: 'El campo solo puede contener caracteres numéricos',
  required: 'Campo obligatorio',
  confirmed: 'La contraseña de confirmación no coincide'
}

tr.messages = {
  ...tr.messages,
  alpha_num: 'Bu alan yalnızca alfanümerik karakterler içerebilir',
  email: 'Bu alan geçerli bir e-posta adresi olmalıdır',
  max_value: 'Bu alan {max} veya daha az olmalıdır',
  max: 'Bu alan {length} karakterden fazla olamaz',
  min_value: 'Bu alan {min} veya daha fazla olmalıdır',
  min: 'Bu alan en az {length} karakter olmalıdır',
  numeric: 'Bu alan yalnızca sayısal karakterler içerebilir',
  required: 'Bu alan zorunludur',
  confirmed: 'Onay şifresi eşleşmiyor'
}

hr.messages = {
  ...hr.messages,
  alpha_num: 'Polje smije sadržavati samo alfanumeričke znakove',
  email: 'Polje mora biti valjana e-mail adresa',
  max_value: 'Polje mora biti {max} ili manje',
  max: 'Polje ne smije imati više od {length} znakova',
  min_value: 'Polje mora biti {min} ili više',
  min: 'Polje mora imati najmanje {length} znakova',
  numeric: 'Polje smije sadržavati samo numeričke znakove',
  required: 'Polje je obavezno',
  confirmed: 'Lozinka za potvrdu se ne podudara'
}

cs.messages = {
  ...cs.messages,
  alpha_num: 'Polje smije sadržavati samo alfanumeričke znakove',
  email: 'Polje mora biti valjana e-mail adresa',
  max_value: 'Polje mora biti {max} ili manje',
  max: 'Polje ne smije imati više od {length} znakova',
  min_value: 'Polje mora biti {min} ili više',
  min: 'Polje mora imati najmanje {length} znakova',
  numeric: 'Polje smije sadržavati samo numeričke znakove',
  required: 'Polje je obavezno',
  confirmed: 'Lozinka za potvrdu se ne podudara'
}

bg.messages = {
  ...bg.messages,
  alpha_num: 'Полето може да съдържа само буквено-цифрови символи',
  email: 'Полето трябва да бъде валиден имейл',
  max_value: 'Полето трябва да бъде {max} или по-малко',
  max: 'Полето не може да бъде по-дълго от {length} символа',
  min_value: 'Полето трябва да бъде {min} или повече',
  min: 'Полето трябва да бъде поне {length} символа',
  numeric: 'Полето може да съдържа само цифри',
  required: 'Полето е задължително',
  confirmed: 'Паролата за потвърждение не съвпада'
}

const messages = {
  en: en,
  ru: ru,
  de: de,
  es: es,
  tr: tr,
  hr: hr,
  cs: cs,
  bg: bg
}

localize(i18n.locale, messages[i18n.locale])

// Register it globally
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
